import React from 'react'
import { XStack, YStack } from 'tamagui'
import { Trans, useLingui } from '@lingui/react/macro'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocStep } from '../shared/DocStep'

const Embeds = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Presentation Embeds let you showcase a Presentation on your own website,
            blog, or conference page. The embed displays the Presentation title,
            presenter name, description, and a link back to the full Presentation
            page on Guild — giving external visitors a preview of the talk without
            leaving your site.
          </Trans>
        </Paragraph>
        <Paragraph size='$5'>
          <Trans>
            Embeds are lightweight widgets that load inside an iframe, so they work
            on any website regardless of framework or CMS. The styling is
            self-contained and automatically adapts to light and dark backgrounds.
          </Trans>
        </Paragraph>
        {/* TODO: Capture screenshot of Presentation page → Share/Embed dialog showing the presentation embed code */}
      </Card>

      <Card gap='$4'>
        <DocSection id='getting-embed-code' title={t`Getting the Embed Code`}>
          <Paragraph size='$5'>
            <Trans>
              To embed a Presentation on an external site, follow these steps.
            </Trans>
          </Paragraph>
          <DocStep
            number={1}
            title={t`Open the Presentation page`}
            description={t`Navigate to the Presentation you want to embed. You can find it through your profile, a Guild page, or by searching for it in the Command Palette.`}
          />
          <DocStep
            number={2}
            title={t`Open the share menu`}
            description={t`Click the Share button on the Presentation page to reveal sharing options, including the embed code.`}
          />
          <DocStep
            number={3}
            title={t`Copy the embed code`}
            description={t`Copy the provided HTML snippet. It includes a script tag and a container element that renders the Presentation widget on your site.`}
          />
          <DocStep
            number={4}
            title={t`Paste into your site`}
            description={t`Add the embed code to any HTML page, blog post, or CMS content block. The widget loads automatically and links back to the full Presentation on Guild.`}
          />
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='embed-use-cases' title={t`When to Use Presentation Embeds`}>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='globe' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Conference websites</Paragraph> — Highlight
                  speaker talks on your event's website with a live preview that stays up to date.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='edit' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Company blogs</Paragraph> — Feature team
                  members' Presentations in blog posts or internal knowledge bases.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='person' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Personal portfolios</Paragraph> — Showcase
                  your speaking history on your personal site with embeds that link back to the full
                  Presentation on Guild.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
          <Paragraph size='$5'>
            <Trans>
              Any updates to the title, description, or video are reflected
              automatically in the embed. For embedding an entire Guild or its
              Events, see{' '}
              <Link href='/docs/guilds/embeds'>Guild Embeds</Link> and{' '}
              <Link href='/docs/events/embeds'>Event Embeds</Link>.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Embeds
